import { languageDefinition, languageScript, type AuthenticatedUserProfile } from "@clinic/shared";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Avatar } from "@clinic/ui/components/avatar";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from "@clinic/ui/components/menu";
import { Icon } from "@clinic/ui/components/icon";
import { PersonName, usePersonName } from "@clinic/ui/components/person-name";
import { WEB_VERSION } from "@web/shared/constants/app";
import { changeLanguage } from "@web/i18n/language";
import { cn } from "@clinic/ui/lib/cn";
import { Ltr } from "@clinic/ui/components/ltr";

export interface UserMenuProps {
  readonly user: AuthenticatedUserProfile;
  readonly onLogout: () => void;
}

export function UserMenu({ user, onLogout }: UserMenuProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const displayName = usePersonName();
  const navigate = useNavigate();
  const current = i18n.language.split("-")[0];

  return (
    <Menu>
      <MenuTrigger
        data-testid="user-menu"
        className={cn(
          "group flex w-full cursor-pointer items-center gap-[11px] rounded-panel p-3",
          "border border-line bg-surface transition-colors duration-150",
          "hover:bg-primary-50 data-[state=open]:bg-primary-50",
        )}
      >
        <Avatar
          data-testid="user-menu-avatar"
          name={displayName(user.name)}
          size={38}
          className="bg-success-100 text-success-800"
        />

        <span className="flex min-w-0 flex-1 flex-col leading-label text-start">
          <PersonName
            name={user.name}
            data-testid="user-menu-name"
            className="truncate text-value font-bold text-ink"
          />
          <span data-testid="user-menu-role" className="truncate text-meta text-ink-muted">
            {t(`roles.${user.role}`)}
          </span>
        </span>

        <Icon
          name="chevron-down"
          className={cn(
            "text-ink-subtle transition-transform duration-150",
            "group-data-[state=open]:rotate-180",
          )}
        />
      </MenuTrigger>

      <MenuContent align="start" data-testid="user-menu-content">
        <MenuItem
          icon="user"
          data-testid="user-menu-profile"
          onSelect={() => void navigate("/profile")}
        >
          {t("nav.profile")}
        </MenuItem>

        <MenuSeparator />

        <MenuLabel>{t("nav.language")}</MenuLabel>

        {user.clinic.locale.languages.map((language) => (
          <MenuItem
            key={language}
            data-testid={`user-menu-language-${language}`}
            icon={languageScript(language) === "arabic" ? "language" : "globe"}
            onSelect={() => void changeLanguage(language)}
            {...(language === current && {
              trailing: <Icon name="check" className="text-primary-600" />,
            })}
          >
            {languageDefinition(language).nativeName}
          </MenuItem>
        ))}

        <MenuSeparator />

        <MenuItem icon="logout" tone="danger" data-testid="user-menu-logout" onSelect={onLogout}>
          {t("nav.logout")}
        </MenuItem>

        <MenuSeparator />

        <p data-testid="user-menu-version" className="px-2 py-1.5 text-meta text-ink-subtle">
          <span>{t("clinic.version")}</span> <Ltr className="font-mono">v{WEB_VERSION}</Ltr>
        </p>
      </MenuContent>
    </Menu>
  );
}
